import { Head } from "vite-react-ssg";
import { Link } from "react-router-dom";
import { Section, SectionHeading, Stat, Pill } from "@/components/kit";
import { Eyebrow } from "@/components/Eyebrow";
import { HeroBackdrop } from "@/components/HeroBackdrop";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import demoHero from "@/assets/hero/demo-hero.jpg";
import {
  DEMOS_HERO,
  DEMOS_STATS,
  DEMO_DOMAINS,
  FEATURED_DEMOS,
  DEMOS_LOAD_NOTE,
} from "@/content/demos";
import lorenzMp4 from "@/assets/video/lorenz.mp4";
import lorenzPoster from "@/assets/video/lorenz-poster.jpg";

export default function Demos() {
  return (
    <>
      <Head>
        <title>Demos — DjiniousLab</title>
        <meta
          name="description"
          content="130+ ready-to-run simulations across 14 engineering and science domains. Load any demo from the in-app dialog and run it instantly on the same engine that powers production."
        />
      </Head>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <HeroBackdrop image={demoHero}>
        <Eyebrow>{DEMOS_HERO.eyebrow}</Eyebrow>
        <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
          {DEMOS_HERO.title}
        </h1>
        <p className="mt-5 text-xl leading-relaxed text-muted-foreground">
          {DEMOS_HERO.intro}
        </p>
      </HeroBackdrop>

      {/* ── Stat strip ───────────────────────────────────────────────────── */}
      <Section wash="plain" bordered containerClassName="py-12">
        <div className="flex flex-wrap items-center justify-center gap-12 sm:gap-20">
          {DEMOS_STATS.map((s) => (
            <Stat key={s.label} value={s.value} label={s.label} />
          ))}
        </div>
      </Section>

      {/* ── See it run (video) ───────────────────────────────────────────── */}
      <Section wash="wash">
        <SectionHeading
          eyebrow="SEE IT RUN"
          title="Lorenz attractor — streamed, then in 3-D"
          intro="Paste the three-ODE system and hit run. The trajectory streams into a live scope as the solver integrates, and the same run renders an interactive 3-D Plotly attractor inline — captured live from the app, no edits."
          align="center"
        />
        <figure className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-2xl border border-border bg-card shadow-lift">
          <video
            className="block w-full"
            src={lorenzMp4}
            poster={lorenzPoster}
            autoPlay
            loop
            muted
            playsInline
            controls
            aria-label="A DjiniousLab notebook running the Lorenz system: the trajectory streams into a live scope, then renders as an interactive 3-D attractor"
          />
        </figure>
      </Section>

      {/* ── Domain catalog ───────────────────────────────────────────────── */}
      <Section wash="surface">
        <SectionHeading
          eyebrow="DOMAIN CATALOG"
          title="14 engineering and science domains"
          intro="Every domain ships multiple canvases and notebooks, exercising the block palette, solver, and output renderers end-to-end."
          align="center"
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {DEMO_DOMAINS.map((domain) => (
            <Link
              key={domain.name}
              to={`/demos/${domain.slug}`}
              className="group block rounded-2xl transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={`${domain.name} — ${domain.count} demos`}
            >
              <Card className="flex h-full flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-extrabold leading-snug text-foreground">
                    {domain.name}
                  </h3>
                  <Pill tone={domain.tone}>{domain.count} demos</Pill>
                </div>
                <ul className="mt-1 space-y-1">
                  {domain.examples.map((ex) => (
                    <li
                      key={ex}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <span
                        className="h-1 w-1 shrink-0 rounded-full bg-primary"
                        aria-hidden="true"
                      />
                      {ex}
                    </li>
                  ))}
                </ul>
                <span className="mt-auto pt-2 text-sm font-semibold text-primary">
                  View all {domain.count}{" "}
                  <span className="inline-block transition-transform duration-200 group-hover:translate-x-0.5">
                    →
                  </span>
                </span>
              </Card>
            </Link>
          ))}
        </div>
      </Section>

      {/* ── Featured demos ───────────────────────────────────────────────── */}
      <Section wash="plain">
        <SectionHeading
          eyebrow="FEATURED DEMOS"
          title="Selected highlights"
          intro="Six demos that show the breadth of the platform — from a full drone autopilot to a parametric CAD workflow."
          align="center"
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURED_DEMOS.map((demo) => (
            <Card
              key={demo.title}
              className="flex flex-col gap-4"
            >
              {demo.image && (
                <figure className="-mx-6 -mt-6 mb-1 overflow-hidden rounded-t-2xl border-b border-border bg-card">
                  <img src={demo.image} alt={demo.imageAlt ?? ""} className="block w-full" loading="lazy" />
                </figure>
              )}
              <div>
                <Pill tone={demo.domainTone}>{demo.domain}</Pill>
              </div>
              <h3 className="text-lg font-extrabold leading-snug text-foreground">
                {demo.title}
              </h3>
              <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
                {demo.description}
              </p>
              <p className="mt-auto border-t border-border pt-3 text-xs font-semibold text-primary">
                {demo.shows}
              </p>
            </Card>
          ))}
        </div>

        {/* Load note */}
        <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-muted-foreground">
          {DEMOS_LOAD_NOTE}
        </p>
      </Section>

      {/* ── CTA band ─────────────────────────────────────────────────────── */}
      <Section wash="wash" bordered>
        <div className="flex flex-col items-center gap-6 text-center">
          <SectionHeading
            title="Ready to run your first simulation?"
            intro="Book a guided demo and we'll walk through the models most relevant to your team."
            align="center"
          />
          <div className="flex flex-wrap justify-center gap-3">
            <Button to="/book-demo">Book a demo</Button>
            <Button to="/features" variant="ghost">
              How it works
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
